import { Injectable } from "@angular/core";

@Injectable({
    providedIn: 'root'
})
export class MarkdownLatexRendererService {
    private readonly latexRegex: RegExp = /(\$\$)([\s\S]+?)\1|(?:\$ )([\s\S]+?)( \$)/g;

    private readonly sqlKeywords = [
        'SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'DROP',
        'ALTER', 'BEGIN', 'END', 'COMMIT', 'ROLLBACK', 'TEMP', 'TABLE', 'GROUP BY',
        'ORDER BY', 'HAVING', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'OUTER', 'UNION',
        'AS', 'ON', 'DISTINCT', 'COUNT', 'SUM', 'AVG', 'MAX', 'MIN', 'PROCEDURE', 'FUNCTION'
    ];

    private readonly SQL_DETECTION_CONFIG = {
        MIN_KEYWORD_COUNT_FOR_MULTIPLE: 2,
        MIN_HEURISTIC_SCORE_THRESHOLD: 3,
        MIN_KEYWORDS_WITH_BEGIN_END: 1
    } as const;

    constructor() { };

    preprocessMathMarkdown(markdown: string): string {
        if (this.isNotLatex(markdown)) {
            return markdown;
        }

        return markdown.replace(this.latexRegex, (_match, dblDollar, displayContent, inlineContent) => {
            const content = displayContent || inlineContent;
            const isSQL = this.looksLikeSQL(content);

            if (isSQL) {
                return _match;
            }

            const escaped = content.replace(/_/g, '\\_');

            return dblDollar ? `$$${escaped}$$` : `$ ${escaped} $`;
        });
    };

    private looksLikeSQL(content: string): boolean {
        if (!content) {
            return false;
        }

        const contentToCheck = content.toUpperCase();
        const sqlPatterns = [
            /^\s*(SELECT|INSERT|UPDATE|DELETE|CREATE|DROP|ALTER|BEGIN|END)\b/im,
            /\bFROM\s+\w+/i,
            /\bWHERE\s+[\w\s=<>!'".]+/i,
            /\bGROUP\s+BY\b/i,
            /\bORDER\s+BY\b/i,
            /\b(COUNT|SUM|AVG|MAX|MIN)\s*\(/i,
            /\bTEMP\s+TABLE\b/i,
            /\bINSERT\s+INTO\b/i,
            /\bCREATE\s+(TABLE|TEMP\s+TABLE|VIEW|INDEX)/i,
            /\bLANGUAGE\s+plpgsql\b/i,
            /\b(PROCEDURE|FUNCTION)\b/i,
            /\bBEGIN\s*[\r\n]/i,
            /\bEND\s*;?\s*$/im,
            /--.*$/m,
            /;\s*$/m,
            /\bAS\s+\w+\s*[,;]/i
        ];

        if (sqlPatterns.some(pattern => pattern.test(content))) {
            return true;
        }

        const sqlKeywordCount = this.sqlKeywords.filter(keyword =>
            contentToCheck.includes(keyword.toUpperCase())
        ).length;

        const sqlIndicators = {
            hasMultipleSqlKeywords: sqlKeywordCount >= this.SQL_DETECTION_CONFIG.MIN_KEYWORD_COUNT_FOR_MULTIPLE,
            hasUnderscores: content.includes('_'),
            hasTableReferences: /\b\w+_\w+\b/.test(content),
            hasSqlComments: /--/.test(content),
            hasSemicolons: /;/.test(content),
            hasCreateTemp: /CREATE\s+TEMP/i.test(content),
            hasBeginEnd: /\bBEGIN\b/i.test(content) && /\bEND\b/i.test(content)
        };

        const heuristicScore = Object.values(sqlIndicators).filter(Boolean).length;

        const meetsHeuristicThreshold = heuristicScore >= this.SQL_DETECTION_CONFIG.MIN_HEURISTIC_SCORE_THRESHOLD;
        const hasBeginEndWithKeywords = sqlIndicators.hasBeginEnd &&
            sqlKeywordCount >= this.SQL_DETECTION_CONFIG.MIN_KEYWORDS_WITH_BEGIN_END;

        return meetsHeuristicThreshold || hasBeginEndWithKeywords;
    }

    private isNotLatex(text: string): boolean {
        const regex = this.latexRegex;
        regex.lastIndex = 0;
        return !regex.test(text);
    };
}
